/**
 * Onboarding step machine — ADR-0049 (skills-first 4-step IA,
 * mandatory verify, gated Complete). Amends ADR-0042 §1 + ADR-0048
 * `OnboardingStep = 0 | 1 | 2` to a 4-state shape:
 *
 *   0 — Add site               (Domain input + opt-in "Analyze my site")
 *   1 — Skills · detect & match (seven defaults + LLM suggestions +
 *                                opt-in "Analyze my site" re-render)
 *   2 — Install + verify       (detection-as-hint preselected tabs +
 *                                ConnectStripeSection + ProofStatusPanel
 *                                — proof-check advances the flow)
 *   3 — Complete               (verify-gated "You're live" hero +
 *                                condensed ProtocolStackSection; or a
 *                                "NOT YET VERIFIED" safety-net face
 *                                when status isn't *_active)
 *
 * The prior `CONNECT_BACKEND_STEP` literal export is REMOVED (the
 * connect-backend path is the `shopify` tab inside step 2). Any
 * external import breaks at compile time on purpose so stale callers
 * surface immediately.
 *
 * Resume semantics (per ADR-0049 §"Resume mapping" with the founder's
 * correction — pending_proof maps to Skills, NOT Install+verify, so
 * the create flow's `setActiveSite(...) → resume effect` chain can
 * never re-introduce the "added a site, jumped past Skills" defect)
 * are owned by `views/onboarding/Onboarding.tsx`'s `resumeStepFor()`;
 * the machine itself is purely positional and clamps to
 * `[0, ONBOARDING_LAST_STEP]`.
 */

export type OnboardingStep = 0 | 1 | 2 | 3;

export const ONBOARDING_LAST_STEP: OnboardingStep = 3;

export interface OnboardingMachine {
  step: OnboardingStep;
  next(): void;
  back(): void;
  goTo(step: OnboardingStep): void;
}

export interface OnboardingMachineOptions {
  initialStep?: OnboardingStep;
  onChange?: (step: OnboardingStep) => void;
}

export function createOnboardingMachine(
  options: OnboardingMachineOptions = {},
): OnboardingMachine {
  const { initialStep = 0, onChange } = options;
  let step: OnboardingStep = initialStep;

  const set = (next: OnboardingStep): void => {
    step = next;
    onChange?.(step);
  };

  const clampForward = (s: OnboardingStep): OnboardingStep => {
    return (s > ONBOARDING_LAST_STEP ? ONBOARDING_LAST_STEP : s) as OnboardingStep;
  };

  const clampBack = (s: OnboardingStep): OnboardingStep => {
    return (s < 0 ? 0 : s) as OnboardingStep;
  };

  return {
    get step(): OnboardingStep {
      return step;
    },
    next(): void {
      set(clampForward((step + 1) as OnboardingStep));
    },
    back(): void {
      set(clampBack((step - 1) as OnboardingStep));
    },
    goTo(target: OnboardingStep): void {
      set(clampForward(clampBack(target)));
    },
  };
}
